import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useJobRef } from '../hooks/useJobRef'
import { supabase } from '../api/supabase'

interface CreateJobModalProps {
  isOpen: boolean
  onClose: () => void
  // Z36: set when opened from a pre-reservation's "Use this" -- the number is fixed, the direction
  // follows its KI/KE prefix, and nothing in here may generate or change a number.
  reservation?: { id: string; job_ref: string } | null
}

type Direction = 'IMP' | 'EXP'
type TransportMode = 'AIR' | 'SEA' | 'ROAD' | 'COURIER'
type JobType = 'SINGLE' | 'CONSOL'

const DIRECTIONS = [
  { value: 'IMP' as const, label: 'Import' },
  { value: 'EXP' as const, label: 'Export' }
]

const TRANSPORT_MODES = [
  { value: 'AIR' as const, label: 'Air' },
  { value: 'SEA' as const, label: 'Sea' },
  { value: 'ROAD' as const, label: 'Road' },
  { value: 'COURIER' as const, label: 'Courier' }
]

const JOB_TYPES = [
  { value: 'SINGLE' as const, label: 'Single' },
  { value: 'CONSOL' as const, label: 'Consolidation (Consol)' }
]

export default function CreateJobModal({ isOpen, onClose, reservation }: CreateJobModalProps) {
  const navigate = useNavigate()
  const { generateJobRef, loading: generating } = useJobRef()
  const reservedDirection: Direction | null = reservation ? (reservation.job_ref.startsWith('KE') ? 'EXP' : 'IMP') : null
  const [chosenDirection, setDirection] = useState<Direction | ''>('')
  const direction: Direction | '' = reservedDirection ?? chosenDirection
  const [transportMode, setTransportMode] = useState<TransportMode | ''>('')
  const [jobType, setJobType] = useState<JobType | ''>('')
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  // Z36: the reserved number can also go into the document-based flow; it arrives locked there too.
  const handleUseInAutoJobs = () => {
    if (!reservation) return
    navigate('/jobs/autojobs', { state: { reservation: { id: reservation.id, job_ref: reservation.job_ref } } })
    onClose()
  }

  const handleCreate = async () => {
    try {
      setError(null)
      
      // Validate all selections
      if (!direction) {
        setError('Please select a direction')
        return
      }
      if (!transportMode) {
        setError('Please select a transport mode')
        return
      }
      if (!jobType) {
        setError('Please select a job type')
        return
      }

      setCreating(true)

      // Debug: Check authentication status
      console.log('🔍 Checking authentication status...')
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      
      if (sessionError) {
        console.error('❌ Session error:', sessionError)
        throw new Error(`Authentication error: ${sessionError.message}`)
      }
      
      if (!session) {
        console.error('❌ No active session found')
        throw new Error('No active session. Please log in first.')
      }
      
      console.log('✅ User authenticated:', {
        user_id: session.user.id,
        email: session.user.email,
        role: session.user.role,
        access_token: session.access_token ? 'present' : 'missing'
      })

      // Debug: Test basic query permission
      console.log('🔍 Testing basic query permission...')
      try {
        const { data: testData, error: testError } = await supabase
          .from('jobs')
          .select('count', { count: 'exact', head: true })
        
        if (testError) {
          console.warn('⚠️ Basic query failed (this may be expected if RLS policies not applied):', testError.message)
          console.log('💡 Continuing anyway - RLS policies may need to be applied in Supabase')
        } else {
          console.log('✅ Basic query successful, count:', testData)
        }
      } catch (permError) {
        console.warn('⚠️ Permission test error (continuing):', permError)
      }

      // Generate Job Reference
      console.log('🔍 Generating job reference...')
      const jobRef = reservation ? reservation.job_ref : await generateJobRef(direction as Direction)
      console.log('✅ Job reference generated:', jobRef)

      // Navigate to job detail page with initial data
      navigate(`/jobs/new`, {
        state: {
          direction,
          transportMode,
          jobType,
          jobRef,
          reservationId: reservation?.id
        }
      })

      // Close modal
      onClose()
    } catch (err) {
      console.error('❌ Error creating job:', err)
      
      // Enhanced error reporting
      if (err instanceof Error) {
        if (err.message.includes('permission denied')) {
          setError('Permission denied. Please ensure you are logged in and have the correct permissions.')
        } else if (err.message.includes('No active session')) {
          setError('Authentication required. Please log in first.')
        } else {
          setError(err.message)
        }
      } else {
        setError('Failed to create job. Check console for details.')
      }
    } finally {
      setCreating(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">{reservation ? 'Create Job from Reserved Number' : 'Create New Job'}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {reservation && (
            <div className="rounded-lg border border-teal-300 bg-teal-50 dark:bg-teal-900/20 dark:border-teal-700 px-4 py-3 text-sm text-teal-900 dark:text-teal-200">
              Using reserved number <span className="font-mono font-bold">{reservation.job_ref}</span>. It cannot be changed here.
            </div>
          )}
          {/* Direction Selection */}
          <div className={reservation ? 'hidden' : ''}>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Direction <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              {DIRECTIONS.map((dir) => (
                <button
                  key={dir.value}
                  onClick={() => setDirection(dir.value)}
                  className={`px-4 py-2 rounded-lg font-medium transition ${
                    direction === dir.value
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {dir.label}
                </button>
              ))}
            </div>
          </div>

          {/* Transport Mode Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Transport Mode <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              {TRANSPORT_MODES.map((mode) => (
                <button
                  key={mode.value}
                  onClick={() => setTransportMode(mode.value)}
                  className={`px-4 py-2 rounded-lg font-medium transition ${
                    transportMode === mode.value
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {mode.label}
                </button>
              ))}
            </div>
          </div>

          {/* Job Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Job Type <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              {JOB_TYPES.map((type) => (
                <button
                  key={type.value}
                  onClick={() => setJobType(type.value)}
                  className={`px-4 py-2 rounded-lg font-medium transition ${
                    jobType === type.value
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-400 px-4 py-3 rounded">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex gap-3 justify-end">
          {reservation && (
            <button
              onClick={handleUseInAutoJobs}
              className="mr-auto px-3 py-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
            >
              Create from a document (AutoJobs) instead
            </button>
          )}
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={creating || generating}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {creating || generating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Creating...
              </>
            ) : (
              'Create Job'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
